import {
  boundingRect,
  fontStack,
  type BoardElement,
  type BoardTheme,
  type Frame,
  type MediaDto,
} from '@praesto/shared'

const WIDTH = 480
const HEIGHT = 300
const PADDING = 1.08

/**
 * Vorschaubild eines Boards.
 *
 * Gezeichnet wird im Client: Er hat das Dokument bereits im Speicher und kennt
 * jedes Element. Serverseitig zu rendern hieße, einen kompletten Browser
 * mitzuliefern, nur um eine Kachel in der Übersicht zu füllen.
 *
 * Videos und eingebettete Seiten lassen sich nicht in ein Canvas zeichnen —
 * ein Video-Frame ist nicht zwingend geladen, ein fremd-domäniger iFrame
 * grundsätzlich nicht auslesbar. Beide bekommen eine ruhige Platzhalterfläche
 * mit Beschriftung; das ist ehrlicher als ein leeres Rechteck.
 */
export async function renderPreview(
  elements: readonly BoardElement[],
  frames: readonly Frame[],
  mediaById: Map<string, MediaDto>,
  background: string,
  theme: BoardTheme,
): Promise<Blob | null> {
  const bounds = boundingRect([...frames, ...elements])
  if (!bounds) return null

  const canvas = document.createElement('canvas')
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = WIDTH * dpr
  canvas.height = HEIGHT * dpr
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.scale(dpr, dpr)

  ctx.fillStyle = background
  ctx.fillRect(0, 0, WIDTH, HEIGHT)

  const scale = Math.min(WIDTH / bounds.w, HEIGHT / bounds.h) / PADDING
  const offsetX = WIDTH / 2 - (bounds.x + bounds.w / 2) * scale
  const offsetY = HEIGHT / 2 - (bounds.y + bounds.h / 2) * scale
  const px = (value: number): number => value * scale
  const toX = (value: number): number => value * scale + offsetX
  const toY = (value: number): number => value * scale + offsetY

  for (const frame of frames) {
    if (frame.background) {
      ctx.fillStyle = frame.background
      ctx.fillRect(toX(frame.x), toY(frame.y), px(frame.w), px(frame.h))
    }
    if (frame.borderWidth > 0) {
      ctx.strokeStyle = frame.borderColor
      ctx.lineWidth = Math.max(0.5, px(frame.borderWidth))
      ctx.strokeRect(toX(frame.x), toY(frame.y), px(frame.w), px(frame.h))
    }
  }

  for (const element of elements) {
    const x = toX(element.x)
    const y = toY(element.y)
    const w = px(element.w)
    const h = px(element.h)

    if (element.type === 'image') {
      const media = mediaById.get(element.mediaId)
      // Die kleinste Stufe reicht: Das Ziel ist 480 px breit.
      const source = media?.variants[0]?.url
      if (source) {
        try {
          const image = await loadImage(source)
          ctx.drawImage(image, x, y, w, h)
          continue
        } catch {
          // Fällt unten auf die Platzhalterfläche zurück.
        }
      }
    }

    if (element.type === 'text') {
      ctx.fillStyle = element.color
      ctx.font = `${element.bold ? '600 ' : ''}${Math.max(4, px(element.fontSize))}px ${fontStack(
        element.font || (element.serif ? theme.headingFont : theme.bodyFont),
      )}`
      ctx.textAlign = element.align === 'center' ? 'center' : element.align === 'right' ? 'right' : 'left'
      const textX = element.align === 'center' ? x + w / 2 : element.align === 'right' ? x + w : x
      ctx.fillText(element.text.split('\n')[0] ?? '', textX, y + px(element.fontSize), w)
      continue
    }

    ctx.fillStyle = 'rgba(128, 128, 128, 0.35)'
    ctx.fillRect(x, y, w, h)
    if (w > 40 && h > 16) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)'
      ctx.font = '10px system-ui, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(element.type === 'video' ? 'Video' : 'Website', x + w / 2, y + h / 2 + 3, w)
    }
  }

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.7))
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = src
  })
}
